// Screens of the personal workspace (the general dashboard) for the homepage,
// recreated in code from the real pages: Scenarios, Library, a debrief,
// Reviews and Progress, each inside the workspace's own rail. Scenario cards
// read the real catalog and its covers; everything else is sample data, not a
// real account. Purely presentational: each scene is one labelled image to
// assistive tech, with no live controls.
import type { ReactNode } from "react";
import {
  AudioLines,
  BarChart3,
  Check,
  Clock,
  FileText,
  House,
  LibraryBig,
  MessageCircle,
  MessageSquare,
  Phone,
  Plus,
  Printer,
  RotateCcw,
  Sparkles,
  UsersRound,
} from "lucide-react";
import type { TFunc } from "@/shared/i18n/useT";
import { CATALOG_SCENARIOS, catalogCoverPath } from "@/platform/scenarioCatalog/catalog";
import type { CatalogScenario } from "@/platform/scenarioCatalog/types";
import { SceneFrame } from "@/shared/pages/solutions/SolutionLanding";

const S = "homeLanding.scenes";

type RailItem = "home" | "scenarios" | "reviews" | "library" | "emery" | "progress";

const RAIL: { id: RailItem; icon: ReactNode }[] = [
  { id: "home", icon: <House size={9} /> },
  { id: "scenarios", icon: <UsersRound size={9} /> },
  { id: "reviews", icon: <AudioLines size={9} /> },
  { id: "library", icon: <LibraryBig size={9} /> },
  { id: "emery", icon: <MessageCircle size={9} /> },
  { id: "progress", icon: <BarChart3 size={9} /> },
];

/** The workspace shell: its rail with the current page lit, a breadcrumb,
 *  and the page itself. */
function WorkspaceFrame({
  t,
  active,
  children,
}: {
  t: TFunc;
  active: RailItem;
  children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[22%_1fr] bg-[#f4f3ef] sm:grid-cols-[19%_1fr]">
      <div className="border-r border-home-line px-2 py-3">
        <p className="px-1.5 font-home-serif text-[11px] font-semibold text-home-navy">EmpatiQ</p>
        <p className="mt-2 px-1.5 text-[7px] font-semibold text-home-ink">{t(`${S}.rail.workspace`)}</p>
        <ul className="mt-2 space-y-0.5">
          {RAIL.map((item) => (
            <li
              key={item.id}
              className={`relative flex items-center gap-1.5 rounded-[4px] px-1.5 py-1 text-[7.5px] ${
                item.id === active ? "bg-home-navy/[0.08] font-semibold text-home-navy" : "text-home-muted"
              }`}
            >
              {item.id === active && (
                <span className="absolute -left-2 top-1/2 h-2.5 w-[2px] -translate-y-1/2 rounded-r bg-home-navy" />
              )}
              {item.icon}
              <span className="truncate">{t(`${S}.rail.${item.id}`)}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="m-1.5 ml-0 overflow-hidden rounded-[6px] border border-home-line bg-white">
        <div className="border-b border-home-line px-3 py-1.5 text-[7px] text-home-muted">
          {t(`${S}.rail.workspace`)} / <span className="font-semibold text-home-ink">{t(`${S}.rail.${active}`)}</span>
        </div>
        <div className="px-3.5 py-3">{children}</div>
      </div>
    </div>
  );
}

function PageHead({ title, sub, action }: { title: string; sub: string; action?: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-home-serif text-[13px] font-semibold leading-tight text-home-navy">{title}</p>
        <p className="mt-1 max-w-[46ch] text-[7.5px] leading-snug text-home-muted">{sub}</p>
      </div>
      {action}
    </div>
  );
}

function DarkButton({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-[3px] bg-home-navy px-2 py-1 text-[7.5px] font-semibold text-white">
      {icon}
      {children}
    </span>
  );
}

/* ── Scenarios ───────────────────────────────────────────────────────────── */

const SHOWN = [
  "crisis-eviction-barricade",
  "crisis-bridge-caller",
  "crisis-welfare-check",
  "crisis-veteran-parking-lot",
  "crisis-workplace-texts",
  "ransomware-first-contact",
];

const CATEGORY_DOT: Record<CatalogScenario["category"], string> = {
  crisis: "bg-[#2f4a7a]",
  ransomware: "bg-[#5f3d94]",
  business: "bg-[#2b6b5e]",
};

const DIFFICULTY_TONE = {
  easy: "bg-emerald-50 text-emerald-800",
  medium: "bg-amber-50 text-amber-800",
  hard: "bg-red-50 text-red-800",
} as const;

export function WorkspaceCatalogScene({ t, label, caption }: { t: TFunc; label: string; caption: string }) {
  const scenarios = SHOWN.map((slug) => CATALOG_SCENARIOS.find((item) => item.slug === slug)).filter(
    (item): item is CatalogScenario => !!item,
  );
  return (
    <SceneFrame label={label} caption={caption}>
      <WorkspaceFrame t={t} active="scenarios">
        <PageHead title={t(`${S}.catalog.title`)} sub={t(`${S}.catalog.sub`)} />
        <div className="mt-2.5 flex flex-wrap gap-1">
          {(["all", "crisis", "ransomware", "business"] as const).map((chip) => (
            <span
              key={chip}
              className={`rounded-full border px-1.5 py-[1px] text-[7px] ${
                chip === "all" ? "border-home-navy/40 bg-home-soft font-semibold text-home-navy" : "border-home-line text-home-muted"
              }`}
            >
              {t(`${S}.catalog.${chip}`)}
            </span>
          ))}
        </div>
        <div className="mt-2.5 grid grid-cols-2 gap-1.5 sm:grid-cols-3">
          {scenarios.map((item, index) => {
            const difficulty = item.exercise.difficulty ?? "medium";
            return (
              <div key={item.slug} className="overflow-hidden rounded-[4px] border border-home-line bg-white">
                <img
                  src={catalogCoverPath(item.slug)}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="aspect-[16/9] w-full bg-home-navy object-cover"
                />
                <div className="p-1.5">
                  <div className="flex flex-wrap items-center gap-1 text-[6.5px] text-home-muted">
                    <span className="inline-flex items-center gap-0.5 font-semibold text-home-ink">
                      <span className={`h-1 w-1 rounded-full ${CATEGORY_DOT[item.category]}`} />
                      {t(`${S}.catalog.${item.category}`)}
                    </span>
                    <span className={`rounded-[2px] px-0.5 font-semibold uppercase ${DIFFICULTY_TONE[difficulty]}`}>
                      {t(`${S}.catalog.${difficulty}`)}
                    </span>
                    {item.mode === "voice" ? <Phone size={6} /> : <MessageSquare size={6} />}
                    <span className="inline-flex items-center gap-0.5">
                      <Clock size={6} />
                      {t(`${S}.catalog.minutes`, { n: item.minutes })}
                    </span>
                  </div>
                  <p className="mt-1 truncate font-home-serif text-[8.5px] font-semibold text-home-navy">
                    {item.exercise.title}
                  </p>
                  <p className="truncate text-[6.5px] text-home-muted">
                    {item.counterpartName} · {item.counterpartLabel}
                  </p>
                  <p className="mt-1 text-[6.5px] font-semibold text-home-ink">
                    {index === 0 ? t(`${S}.catalog.best`, { n: 74 }) : (
                      <span className="font-normal text-home-muted">{t(`${S}.catalog.notTried`)}</span>
                    )}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </WorkspaceFrame>
    </SceneFrame>
  );
}

/* ── Library ─────────────────────────────────────────────────────────────── */

const MATERIALS = [
  { key: "sop", category: "guides", type: "PDF", selected: true },
  { key: "checklist", category: "guides", type: "MD", selected: true },
  { key: "policy", category: "policies", type: "MD", selected: false },
] as const;

export function WorkspaceLibraryScene({ t, label, caption }: { t: TFunc; label: string; caption: string }) {
  return (
    <SceneFrame label={label} caption={caption}>
      <WorkspaceFrame t={t} active="library">
        <PageHead
          title={t(`${S}.library.title`)}
          sub={t(`${S}.library.sub`)}
          action={<DarkButton icon={<Plus size={8} />}>{t(`${S}.library.add`)}</DarkButton>}
        />
        <div className="mt-3 overflow-hidden rounded-[4px] border border-home-line">
          <div className="grid grid-cols-[1fr_28%_16%_10%] gap-2 bg-home-soft/70 px-2 py-1 text-[6.5px] font-semibold uppercase tracking-[0.06em] text-home-muted">
            <span>{t(`${S}.library.name`)}</span>
            <span>{t(`${S}.library.category`)}</span>
            <span>{t(`${S}.library.status`)}</span>
            <span />
          </div>
          {MATERIALS.map((m) => (
            <div
              key={m.key}
              className={`grid grid-cols-[1fr_28%_16%_10%] items-center gap-2 border-t border-home-line px-2 py-1.5 text-[7.5px] ${
                m.selected ? "bg-home-navy/[0.04]" : ""
              }`}
            >
              <span className="flex min-w-0 items-center gap-1.5 font-semibold text-home-navy">
                <span
                  className={`inline-flex h-2.5 w-2.5 shrink-0 items-center justify-center rounded-[2px] border ${
                    m.selected ? "border-home-navy bg-home-navy text-white" : "border-home-line"
                  }`}
                >
                  {m.selected && <Check size={7} strokeWidth={3} />}
                </span>
                <FileText size={8} className="shrink-0 text-home-muted" />
                <span className="truncate">{t(`${S}.library.${m.key}`)}</span>
              </span>
              <span>
                <span className="rounded-full border border-home-line px-1 py-[1px] text-[6.5px] text-home-muted">
                  {t(`${S}.library.${m.category}`)}
                </span>
              </span>
              <span>
                <span className="rounded-[2px] bg-emerald-50 px-1 py-[1px] text-[6.5px] font-semibold text-emerald-800">
                  {t(`${S}.library.ready`)}
                </span>
              </span>
              <span className="text-[6.5px] text-home-muted">{m.type}</span>
            </div>
          ))}
        </div>
        <div className="mt-2.5 flex items-center justify-between gap-2 rounded-[4px] border border-home-navy/20 bg-home-soft px-2 py-1.5">
          <span className="text-[7.5px] font-semibold text-home-navy">{t(`${S}.library.selected`)}</span>
          <DarkButton icon={<Sparkles size={8} />}>{t(`${S}.library.build`)}</DarkButton>
        </div>
      </WorkspaceFrame>
    </SceneFrame>
  );
}

/* ── Debrief ─────────────────────────────────────────────────────────────── */

const CRITERIA = [
  { key: "c1", score: 82 },
  { key: "c2", score: 60 },
  { key: "c3", score: 78 },
  { key: "c4", score: 68 },
] as const;

export function WorkspaceDebriefScene({ t, label, caption }: { t: TFunc; label: string; caption: string }) {
  return (
    <SceneFrame label={label} caption={caption}>
      <WorkspaceFrame t={t} active="scenarios">
        <p className="text-[7px] text-home-muted">← {t(`${S}.debrief.back`)} · {t(`${S}.debrief.meta`)}</p>
        <p className="mt-1 font-home-serif text-[13px] font-semibold leading-tight text-home-navy">{t(`${S}.debrief.title`)}</p>
        <p className="text-[7px] text-home-muted">{t(`${S}.debrief.who`)}</p>
        <div className="mt-2 flex items-center gap-2">
          <span className="font-home-serif text-[16px] font-semibold leading-none text-home-navy">
            74<span className="text-[8px] font-normal text-home-muted">/100</span>
          </span>
          <span className="h-1 w-16 overflow-hidden rounded-full bg-home-line">
            <span className="block h-full w-[74%] rounded-full bg-home-navy" />
          </span>
          <span className="rounded-full bg-home-soft px-1.5 py-[1px] text-[6.5px] font-semibold text-home-ink">
            {t(`${S}.debrief.outcome`)}
          </span>
        </div>
        <div className="mt-2 flex gap-1.5">
          <DarkButton icon={<RotateCcw size={7} />}>{t(`${S}.debrief.again`)}</DarkButton>
          <span className="inline-flex items-center gap-1 rounded-[3px] px-1 py-1 text-[7.5px] font-semibold text-home-navy">
            <MessageCircle size={8} />
            {t(`${S}.debrief.emery`)}
          </span>
        </div>
        <p className="mt-2.5 border-t border-home-line pt-2 font-home-serif text-[9px] font-semibold text-home-navy">
          {t(`${S}.debrief.summary`)}
        </p>
        <p className="mt-0.5 text-[7.5px] leading-snug text-home-ink">{t(`${S}.debrief.summaryBody`)}</p>
        <p className="mt-2.5 border-t border-home-line pt-2 font-home-serif text-[9px] font-semibold text-home-navy">
          {t(`${S}.debrief.criteria`)}
        </p>
        <ul className="mt-1 space-y-1.5">
          {CRITERIA.map((c) => (
            <li key={c.key}>
              <div className="flex justify-between gap-2 text-[7.5px] font-semibold text-home-ink">
                <span>{t(`${S}.debrief.${c.key}`)}</span>
                <span className="tabular-nums">{c.score}</span>
              </div>
              <div className="mt-0.5 h-[3px] overflow-hidden rounded-full bg-home-line">
                <div className="h-full rounded-full bg-home-navy" style={{ width: `${c.score}%` }} />
              </div>
              <p className="mt-0.5 text-[6.5px] text-home-muted">{t(`${S}.debrief.${c.key}Why`)}</p>
            </li>
          ))}
        </ul>
      </WorkspaceFrame>
    </SceneFrame>
  );
}

/* ── Reviews ─────────────────────────────────────────────────────────────── */

const REVIEW_ROWS = [
  { key: "row1", icon: <AudioLines size={8} />, score: 35, status: "row1Status" },
  { key: "row2", icon: <FileText size={8} />, score: 71, status: "row2Status" },
  { key: "row3", icon: <AudioLines size={8} />, score: null, status: "transcriptOnly" },
] as const;

export function WorkspaceReviewsScene({ t, label, caption }: { t: TFunc; label: string; caption: string }) {
  return (
    <SceneFrame label={label} caption={caption}>
      <WorkspaceFrame t={t} active="reviews">
        <PageHead
          title={t(`${S}.reviews.title`)}
          sub={t(`${S}.reviews.sub`)}
          action={<DarkButton icon={<Plus size={8} />}>{t(`${S}.reviews.newReview`)}</DarkButton>}
        />
        <div className="mt-2.5 inline-flex gap-0.5 rounded-[4px] border border-home-line p-0.5">
          <span className="inline-flex items-center gap-1 rounded-[3px] px-1.5 py-0.5 text-[7px] text-home-muted">
            <AudioLines size={7} />
            {t(`${S}.reviews.audio`)}
          </span>
          <span className="inline-flex items-center gap-1 rounded-[3px] bg-home-navy px-1.5 py-0.5 text-[7px] font-semibold text-white">
            <FileText size={7} />
            {t(`${S}.reviews.text`)}
          </span>
        </div>
        <ul className="mt-2.5 space-y-1.5">
          {REVIEW_ROWS.map((row) => (
            <li key={row.key} className="flex items-center gap-2 rounded-[4px] border border-home-line px-2 py-1.5">
              <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] bg-home-soft text-home-muted">
                {row.icon}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[8px] font-semibold text-home-navy">{t(`${S}.reviews.${row.key}`)}</span>
                <span className="block truncate text-[6.5px] text-home-muted">{t(`${S}.reviews.${row.key}Meta`)}</span>
              </span>
              <span className="shrink-0 rounded-full bg-home-soft px-1.5 py-[1px] text-[6.5px] text-home-ink">
                {t(`${S}.reviews.${row.status}`)}
              </span>
              {row.score !== null && (
                <span className="w-9 shrink-0 text-right font-home-serif text-[9px] font-semibold tabular-nums text-home-navy">
                  {row.score}/100
                </span>
              )}
            </li>
          ))}
        </ul>
      </WorkspaceFrame>
    </SceneFrame>
  );
}

/* ── Progress ────────────────────────────────────────────────────────────── */

/** Score per scored conversation, oldest first; `real` marks a reviewed call. */
const POINTS: { score: number; real?: boolean }[] = [
  { score: 54 }, { score: 62 }, { score: 68 }, { score: 82 }, { score: 73 },
  { score: 48 }, { score: 79 }, { score: 61 }, { score: 84 }, { score: 35, real: true }, { score: 74 },
];

export function WorkspaceProgressScene({ t, label, caption }: { t: TFunc; label: string; caption: string }) {
  const width = 300;
  const height = 92;
  const x = (i: number) => 14 + (i * (width - 22)) / (POINTS.length - 1);
  const y = (score: number) => 6 + ((100 - score) * (height - 14)) / 100;
  const line = POINTS.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(p.score).toFixed(1)}`).join(" ");
  const stats = [
    { key: "practices", value: "10" },
    { key: "real", value: "1" },
    { key: "average", value: "65" },
    { key: "best", value: "84" },
  ];
  return (
    <SceneFrame label={label} caption={caption}>
      <WorkspaceFrame t={t} active="progress">
        <PageHead
          title={t(`${S}.progress.title`)}
          sub={t(`${S}.progress.sub`)}
          action={
            <span className="inline-flex shrink-0 items-center gap-1 rounded-[3px] border border-home-line px-1.5 py-1 text-[7px] text-home-ink">
              <Printer size={7} />
              {t(`${S}.progress.report`)}
            </span>
          }
        />
        <div className="mt-2.5 grid grid-cols-4 gap-1.5">
          {stats.map((stat) => (
            <div key={stat.key} className="rounded-[4px] border border-home-line px-1.5 py-1.5">
              <p className="truncate text-[6px] uppercase tracking-[0.06em] text-home-muted">{t(`${S}.progress.${stat.key}`)}</p>
              <p className="mt-0.5 font-home-serif text-[13px] leading-none text-home-navy">{stat.value}</p>
            </div>
          ))}
        </div>
        <p className="mt-2.5 font-home-serif text-[9px] font-semibold text-home-navy">{t(`${S}.progress.chart`)}</p>
        <div className="mt-1 rounded-[4px] border border-home-line px-1.5 py-1.5">
          <svg viewBox={`0 0 ${width} ${height}`} className="h-auto w-full">
            {[0, 25, 50, 75, 100].map((tick) => (
              <g key={tick}>
                <line x1={12} x2={width - 4} y1={y(tick)} y2={y(tick)} stroke="#e5e2da" strokeWidth={0.6} />
                <text x={0} y={y(tick) + 2} fontSize={5.5} fill="#8a8f98">{tick}</text>
              </g>
            ))}
            <path d={line} fill="none" stroke="#0c1a33" strokeWidth={1.3} />
            {POINTS.map((p, i) => (
              <circle
                key={i}
                cx={x(i)}
                cy={y(p.score)}
                r={p.real ? 2.6 : 2}
                fill={p.real ? "#fff" : "#0c1a33"}
                stroke="#0c1a33"
                strokeWidth={p.real ? 1.1 : 0}
              />
            ))}
          </svg>
          <p className="mt-1 flex gap-3 text-[6.5px] text-home-muted">
            <span className="inline-flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-home-navy" />
              {t(`${S}.progress.legendPractice`)}
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full border border-home-navy bg-white" />
              {t(`${S}.progress.legendReal`)}
            </span>
          </p>
        </div>
      </WorkspaceFrame>
    </SceneFrame>
  );
}
